import React, { useState } from 'react';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Package, Lock, CheckCircle2, Circle, Mail, AlertTriangle, Send, Loader2, Users } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { formatDate, getMovementTypeColor, getMovementTypeLabel } from '../../lib/utils';

const WORKFLOW_STEPS = [
  { key: 'salida',     label: 'Salida',     infoKey: null },
  { key: 'entregado',  label: 'Entregado',  infoKey: 'delivery_info' },
  { key: 'retorno',    label: 'Retorno',    infoKey: 'return_info' },
  { key: 'completado', label: 'Completado', infoKey: 'verification_info' },
];

// Legacy 'verificado' is normalized to 'completado'
const _normalizeStatus = (s) => (s === 'verificado' ? 'completado' : s);

const formatTimestamp = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const getConditionBadge = (condition) => {
  const styles = {
    bueno: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    regular: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    falla: 'bg-red-500/20 text-red-400 border-red-500/30',
    faltante: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    perdido: 'bg-red-600/20 text-red-400 border-red-600/30'
  };
  const labels = {
    bueno: 'Bueno',
    regular: 'Regular',
    falla: 'Con Falla',
    faltante: 'Faltante',
    perdido: 'Perdido'
  };
  return <Badge className={styles[condition] || styles.bueno}>{labels[condition] || condition}</Badge>;
};

export const MovementViewModal = ({ open, onOpenChange, movement, onResendReminder, onFetchReminderRecipients }) => {
  const [resending, setResending] = useState({ creator: false, directors: false });
  // v5.4.32 — Confirmation dialog before manual director escalation
  // v5.4.33 — Per-recipient selection via checkboxes (default all checked)
  const [confirmEscalation, setConfirmEscalation] = useState({
    open: false,
    recipients: [],
    loading: false,
    alreadySent: false,
    selectedEmails: new Set(),  // ← selected recipients
  });

  const handleResend = async (target) => {
    if (!onResendReminder) return;
    if (target === 'directors') {
      // Show a confirmation modal with the recipient list first
      setConfirmEscalation({ open: true, recipients: [], loading: true, alreadySent: !!movement?.return_reminder_directors_sent_at, selectedEmails: new Set() });
      try {
        const data = onFetchReminderRecipients ? await onFetchReminderRecipients(movement) : null;
        const recips = data?.directors || [];
        setConfirmEscalation({
          open: true,
          recipients: recips,
          loading: false,
          alreadySent: !!movement?.return_reminder_directors_sent_at,
          selectedEmails: new Set(recips.map((r) => r.email)),  // default: all checked
        });
      } catch (_e) {
        setConfirmEscalation({ open: true, recipients: [], loading: false, alreadySent: false, selectedEmails: new Set() });
      }
      return;
    }
    setResending((s) => ({ ...s, [target]: true }));
    try {
      await onResendReminder(movement, target);
    } finally {
      setResending((s) => ({ ...s, [target]: false }));
    }
  };

  const toggleRecipient = (email) => {
    setConfirmEscalation((c) => {
      const next = new Set(c.selectedEmails);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return { ...c, selectedEmails: next };
    });
  };

  const toggleAllRecipients = () => {
    setConfirmEscalation((c) => {
      const all = c.recipients.map((r) => r.email);
      const allChecked = all.every((e) => c.selectedEmails.has(e));
      return {
        ...c,
        selectedEmails: allChecked ? new Set() : new Set(all),
      };
    });
  };

  const confirmSendToDirectors = async () => {
    setResending((s) => ({ ...s, directors: true }));
    const selected = Array.from(confirmEscalation.selectedEmails);
    setConfirmEscalation((c) => ({ ...c, open: false }));
    try {
      await onResendReminder(movement, 'directors', selected);
    } finally {
      setResending((s) => ({ ...s, directors: false }));
    }
  };
  if (!movement) return null;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detalle del Movimiento</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Event Information Card */}
          <Card className="bg-primary/5 border-primary/20">
            <CardHeader className="py-3">
              <CardTitle className="text-sm text-primary">Información del Evento</CardTitle>
            </CardHeader>
            <CardContent className="pb-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <Label className="text-muted-foreground text-xs">Nombre del Evento</Label>
                  <p className="font-medium">{movement.event_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Fecha del Evento</Label>
                  <p className="font-medium">{movement.event_date ? formatDate(movement.event_date) : '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Cliente</Label>
                  <p className="font-medium">{movement.client_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Contacto</Label>
                  <p className="font-medium">{movement.client_contact || '-'}</p>
                </div>
                {movement.control_number && (
                  <div className="col-span-2">
                    <Label className="text-muted-foreground text-xs">Número de Control</Label>
                    <p className="font-medium">{movement.control_number}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Workflow Timeline (only for type=salida) */}
          {movement.type === 'salida' && (
            <Card className="bg-muted/30 border">
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Trazabilidad del Movimiento</CardTitle>
              </CardHeader>
              <CardContent className="pb-4">
                <div className="space-y-3">
                  {WORKFLOW_STEPS.map((step, idx) => {
                    const currentStatus = _normalizeStatus(movement.status || movement.workflow_status) || 'salida';
                    const currentIdx = WORKFLOW_STEPS.findIndex(s => s.key === currentStatus);
                    const reached = idx <= currentIdx;
                    const info = step.infoKey ? movement[step.infoKey] : null;
                    const personName = step.key === 'salida'
                      ? (movement.responsible_name || movement.verified_by_name || '—')
                      : (info?.user_name || info?.responsible_name || '—');
                    const stamp = step.key === 'salida'
                      ? movement.date
                      : info?.timestamp;
                    return (
                      <div key={step.key} className="flex items-start gap-3" data-testid={`workflow-step-${step.key}`}>
                        <div className="mt-0.5">
                          {reached ? (
                            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                          ) : (
                            <Circle className="h-5 w-5 text-muted-foreground/40" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${reached ? '' : 'text-muted-foreground'}`}>
                              {step.label}
                            </span>
                            {idx === currentIdx && (
                              <Badge className="bg-primary/15 text-primary text-[10px]">Actual</Badge>
                            )}
                          </div>
                          {reached && (
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {personName}
                              {stamp && <span> · {formatTimestamp(stamp)}</span>}
                              {step.key === 'salida' && (movement.receipt_info?.user_name || movement.receiver_user_name) && (
                                <div data-testid="workflow-receipt-line">
                                  Recibió del almacén: {movement.receipt_info?.user_name || movement.receiver_user_name}
                                  {movement.receipt_info?.timestamp && <span> · {formatTimestamp(movement.receipt_info.timestamp)}</span>}
                                  {!movement.receipt_info && movement.receiver_user_name && (
                                    <span className="text-amber-500"> (pendiente de confirmar)</span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* v5.4.29 — Notificaciones automáticas (recordatorio de retorno + escalación) */}
          {/* v5.4.31 — Also show for salidas without a completed return so admins can manually resend */}
          {movement.type === 'salida' && !movement.has_return && (
            <Card className="bg-amber-500/5 border-amber-500/30" data-testid="return-reminder-timeline">
              <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Mail className="h-4 w-4 text-amber-500" />
                  Notificaciones automáticas de retorno
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-4">
                <div className="space-y-3">
                  {/* Creator entry */}
                  <div className="flex items-start gap-3" data-testid="reminder-creator-entry">
                    <div className="mt-0.5">
                      <Mail className={`h-5 w-5 ${movement.return_reminder_creator_sent_at ? 'text-blue-500' : 'text-muted-foreground/40'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">Recordatorio al creador</span>
                        {movement.return_reminder_creator_sent_at ? (
                          <Badge className="bg-blue-500/15 text-blue-500 border-blue-500/30 text-[10px]">
                            enviado
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] text-muted-foreground">
                            pendiente
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {movement.responsible_name || '—'}
                        {movement.return_reminder_creator_sent_at && (
                          <span> · {formatTimestamp(movement.return_reminder_creator_sent_at)}</span>
                        )}
                      </p>
                    </div>
                    {onResendReminder && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResend('creator')}
                        disabled={resending.creator}
                        className="h-7 px-2 text-xs"
                        data-testid="resend-creator-btn"
                      >
                        {resending.creator ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Send className="h-3 w-3 mr-1" />
                        )}
                        {movement.return_reminder_creator_sent_at ? 'Reenviar' : 'Enviar ahora'}
                      </Button>
                    )}
                  </div>

                  {/* Director escalation entry */}
                  <div className="flex items-start gap-3" data-testid="reminder-director-entry">
                    <div className="mt-0.5">
                      <AlertTriangle className={`h-5 w-5 ${movement.return_reminder_directors_sent_at ? 'text-red-500' : 'text-muted-foreground/40'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">Escalación a dirección</span>
                        {movement.return_reminder_directors_sent_at ? (
                          <Badge className="bg-red-500/15 text-red-500 border-red-500/30 text-[10px]">
                            escalado
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] text-muted-foreground">
                            pendiente
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Admins / Superadmins
                        {movement.return_reminder_directors_sent_at && (
                          <span> · {formatTimestamp(movement.return_reminder_directors_sent_at)}</span>
                        )}
                      </p>
                    </div>
                    {onResendReminder && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResend('directors')}
                        disabled={resending.directors}
                        className="h-7 px-2 text-xs"
                        data-testid="resend-director-btn"
                      >
                        {resending.directors ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Send className="h-3 w-3 mr-1" />
                        )}
                        {movement.return_reminder_directors_sent_at ? 'Reenviar' : 'Escalar ahora'}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Transition Notes — all notes captured during workflow transitions */}
          {movement.type === 'salida' && (() => {
            const stepStyles = {
              salida:     'bg-amber-500/15 text-amber-400 border-amber-500/30',
              entregado:  'bg-blue-500/15 text-blue-400 border-blue-500/30',
              retorno:    'bg-purple-500/15 text-purple-400 border-purple-500/30',
              completado: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
            };
            const noteEntries = [];
            // Salida creation note (movement.notes)
            if (movement.notes && movement.notes.trim()) {
              noteEntries.push({
                step: 'salida',
                label: 'Salida',
                author: movement.responsible_name || '—',
                ts: movement.date,
                note: movement.notes,
              });
            }
            const rc = movement.receipt_info;
            if (rc?.notes && rc.notes.trim()) {
              noteEntries.push({
                step: 'salida',
                label: 'Recibió del almacén',
                author: rc.user_name || '—',
                ts: rc.timestamp,
                note: rc.notes,
              });
            }
            const di = movement.delivery_info;
            if (di?.notes && di.notes.trim()) {
              noteEntries.push({
                step: 'entregado',
                label: 'Entregado',
                author: di.user_name || '—',
                ts: di.timestamp,
                note: di.notes,
              });
            }
            const ri = movement.return_info;
            if (ri?.notes && ri.notes.trim()) {
              noteEntries.push({
                step: 'retorno',
                label: 'Retorno',
                author: ri.user_name || '—',
                ts: ri.timestamp,
                note: ri.notes,
              });
            }
            const vi = movement.verification_info;
            if (vi?.notes && vi.notes.trim()) {
              noteEntries.push({
                step: 'completado',
                label: 'Completado',
                author: vi.user_name || vi.responsible_name || '—',
                ts: vi.timestamp,
                note: vi.notes,
              });
            }
            return (
              <Card className="bg-muted/30 border" data-testid="transition-notes-card">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">Notas de las transiciones</CardTitle>
                </CardHeader>
                <CardContent className="pb-4 space-y-3">
                  {noteEntries.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic" data-testid="transition-notes-empty">
                      Aún no se han registrado notas en las transiciones de este movimiento.
                    </p>
                  ) : (
                    noteEntries.map((entry, idx) => (
                      <div key={idx} className="flex gap-3" data-testid={`transition-note-${entry.step}`}>
                        <Badge className={`${stepStyles[entry.step]} h-5 px-2 self-start whitespace-nowrap`}>
                          {entry.label}
                        </Badge>
                        <div className="flex-1 min-w-0 text-xs">
                          <div className="text-muted-foreground">
                            {entry.author}
                            {entry.ts && <span> · {formatTimestamp(entry.ts)}</span>}
                          </div>
                          <div className="mt-0.5 text-foreground italic whitespace-pre-wrap break-words">
                            “{entry.note}”
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            );
          })()}

          {/* Movement Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground">Tipo</Label>
              <p className="mt-1">
                <Badge className={getMovementTypeColor(movement.type)}>
                  {getMovementTypeLabel(movement.type)}
                </Badge>
              </p>
            </div>
            <div>
              <Label className="text-muted-foreground">Fecha de Registro</Label>
              <p className="mt-1 font-medium">{formatDate(movement.date)}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Responsable</Label>
              <p className="mt-1 font-medium">{movement.responsible_name}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Autorizado por</Label>
              <p className="mt-1 font-medium">{movement.verified_by_name || '-'}</p>
            </div>
          </div>

          {/* Admin Override Info */}
          {movement.admin_override && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-amber-500" />
                <div>
                  <p className="text-sm font-medium text-amber-400">Override de Administrador</p>
                  <p className="text-xs text-muted-foreground">
                    Autorizado por: <strong>{movement.override_by_name || movement.override_by || 'Admin'}</strong>
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {movement.salida_info && (
            <div className="p-3 bg-blue-500/10 rounded-lg">
              <Label className="text-muted-foreground text-xs">Salida Relacionada</Label>
              <p className="text-sm">
                {formatDate(movement.salida_info.date)} - {movement.salida_info.responsible_name}
              </p>
            </div>
          )}
          
          {movement.notes && (
            <div>
              <Label className="text-muted-foreground">Notas</Label>
              <p className="mt-1">{movement.notes}</p>
            </div>
          )}

          <div>
            <Label className="text-muted-foreground">Equipos ({movement.items?.length || 0})</Label>
            <div className="mt-2 border rounded-md divide-y max-h-64 overflow-y-auto">
              {movement.items?.map((item, index) => (
                <div key={index} className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">{item.equipment_name}</p>
                        <p className="text-xs text-muted-foreground">
                          Cantidad: {item.quantity} {item.location_name && `| Ubicación: ${item.location_name}`}
                        </p>
                        {item.serial_numbers && (
                          <p className="text-xs text-muted-foreground font-mono">
                            Series: {item.serial_numbers}
                          </p>
                        )}
                        {item.receptor_numbers && (
                          <p className="text-xs text-muted-foreground font-mono">
                            No. Equipo: {item.receptor_numbers}
                          </p>
                        )}
                      </div>
                    </div>
                    {getConditionBadge(item.condition)}
                  </div>
                  {item.fault_reason && (
                    <p className="mt-2 text-xs text-red-400 bg-red-500/10 p-2 rounded">
                      Razón: {item.fault_reason}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* v5.4.32 — Confirmation dialog for manual director escalation */}
    <AlertDialog open={confirmEscalation.open} onOpenChange={(v) => setConfirmEscalation((c) => ({ ...c, open: v }))}>
      <AlertDialogContent className="max-w-md" data-testid="escalation-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            {confirmEscalation.alreadySent ? 'Re-escalar a Dirección' : 'Escalar a Dirección'}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p>
                {confirmEscalation.alreadySent
                  ? 'Este movimiento ya fue escalado previamente. Al reenviar, los directores recibirán una nueva copia.'
                  : 'Estás a punto de disparar la escalación manualmente. Los siguientes destinatarios recibirán el correo:'}
              </p>
              {confirmEscalation.loading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : confirmEscalation.recipients.length === 0 ? (
                <div className="rounded-md bg-amber-500/10 border border-amber-500/30 p-3 text-amber-600 text-xs">
                  No hay admins/superadmins activos con correo configurado. La escalación no se enviará.
                </div>
              ) : (
                <div className="rounded-md border p-3 bg-muted/40 max-h-56 overflow-y-auto">
                  <div className="flex items-center justify-between gap-2 pb-2 border-b mb-2">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span className="text-xs font-medium text-muted-foreground">
                        {confirmEscalation.selectedEmails.size} de {confirmEscalation.recipients.length} seleccionado(s)
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={toggleAllRecipients}
                      className="text-xs text-primary hover:underline font-medium"
                      data-testid="escalation-toggle-all"
                    >
                      {confirmEscalation.recipients.every((r) => confirmEscalation.selectedEmails.has(r.email))
                        ? 'Deseleccionar todos'
                        : 'Seleccionar todos'}
                    </button>
                  </div>
                  <ul className="space-y-1.5">
                    {confirmEscalation.recipients.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs" data-testid={`escalation-recipient-${i}`}>
                        <Checkbox
                          checked={confirmEscalation.selectedEmails.has(r.email)}
                          onCheckedChange={() => toggleRecipient(r.email)}
                          className="mt-0.5"
                          data-testid={`escalation-recipient-checkbox-${i}`}
                        />
                        <Badge
                          variant="outline"
                          className={`text-[9px] shrink-0 mt-0.5 ${
                            r.role === 'superadmin' ? 'border-red-500/50 text-red-600' : 'border-blue-500/50 text-blue-600'
                          }`}
                        >
                          {r.role === 'superadmin' ? 'SUPER' : 'ADMIN'}
                        </Badge>
                        <div
                          className="min-w-0 flex-1 cursor-pointer"
                          onClick={() => toggleRecipient(r.email)}
                        >
                          <p className="font-medium truncate">{r.name}</p>
                          <p className="text-muted-foreground truncate">{r.email}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="escalation-cancel-btn">Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={confirmSendToDirectors}
            disabled={confirmEscalation.loading || confirmEscalation.selectedEmails.size === 0}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="escalation-confirm-btn"
          >
            <Send className="h-3.5 w-3.5 mr-1.5" />
            Enviar a {confirmEscalation.selectedEmails.size} destinatario(s)
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>
  );
};

export default MovementViewModal;
